import { useState, useEffect } from "react";
import ContentRenders from "./ContentRenders";
import { useResource } from "../../../contexts/ResourceContext";
import { debugLog } from "../../../../../configs/loggingConfig";

// Helper function to determine MIME type from file extension
const getMimeTypeFromExtension = (filename) => {
  if (!filename) return "image/jpeg"; // default fallback
  
  const extension = filename.toLowerCase().split('.').pop();
  const mimeTypes = {
    'svg': 'image/svg+xml',
    'png': 'image/png',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'bmp': 'image/bmp',
    'ico': 'image/x-icon',
    'tiff': 'image/tiff',
    'tif': 'image/tiff'
  };
  
  return mimeTypes[extension] || "image/jpeg";
};

// Helper function to find filename in content array
const findFilenameInContent = (content) => {
  const textContent = content.find(item => item.type === "text");
  return textContent?.text || "";
};

// Component for rendering resource references with authentication
const ResourceReferenceRender = ({ contentItem }) => {
  const { fetchResourceBlobUrl } = useResource();
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let objectUrl;

    const fetchResource = async () => {
      try {
        setLoading(true);
        setError(null);
        objectUrl = await fetchResourceBlobUrl(contentItem.resourceId);
        setBlobUrl(objectUrl);
        setLoading(false);
      } catch (err) {
        debugLog.error('Error fetching resource:', err);
        setError('Failed to load resource');
        setLoading(false);
      }
    };

    fetchResource();

    // Cleanup: revoke object URL when component unmounts
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [contentItem.resourceId, fetchResourceBlobUrl]);

  if (loading) {
    return <div className="m-chat-bubble__resource-loading">Loading resource...</div>;
  }

  if (error) {
    return (
      <div className="m-chat-bubble__resource-error">
        <span>⚠️ {error}</span>
        {contentItem.resourceName && <p>{contentItem.resourceName}</p>}
      </div>
    );
  }

  // Determine if it's an image
  const isImage = contentItem.resourceType?.startsWith('image/');

  if (isImage) {
    return (
      <div className="m-chat-bubble__image-wrapper">
        <img
          className="m-chat-bubble__image"
          src={blobUrl}
          alt={contentItem.resourceDescription || contentItem.resourceName || "Resource image"}
        />
      </div>
    );
  } else {
    // Provide download for non-image files
    const handleDownload = () => {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = contentItem.resourceName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    return (
      <div className="m-chat-bubble__file-download">
        <button
          onClick={handleDownload}
          style={{
            background: 'none',
            border: 'none',
            color: '#0066cc',
            textDecoration: 'underline',
            cursor: 'pointer',
            padding: 0,
            font: 'inherit'
          }}
        >
          📎 Download: {contentItem.resourceName || "File"}
        </button>
        {contentItem.resourceDescription && (
          <p style={{ fontSize: "0.9em", color: "#666", marginTop: "0.5em" }}>
            {contentItem.resourceDescription}
          </p>
        )}
      </div>
    );
  }
};

const ChatEntryMsg = ({ content, isUser, hideContentType = [] }) => {
  return (
    <>
      {content
        .filter(contentItem => !hideContentType.includes(contentItem.type)) // Filter out hidden content types
        .sort((a, b) => a.contentOrder - b.contentOrder)
        .map((contentItem, index) => {
          if (contentItem.type === "text") {
            return (
              <ContentRenders
                key={index}
                content={contentItem.text}
                isUser={isUser}
              />
            );
          } else if (contentItem.type === "image_url") {
            return (
              <div className="m-chat-bubble__image-wrapper" key={index}>
                <img
                  className="m-chat-bubble__image"
                  src={`${contentItem.imageUrl}`}
                  alt={contentItem.imageTextDescription}
                />
              </div>
            );
          } else if (contentItem.type === "image_bytes") {
            // Use the provided mimeType or try to determine from filename, fallback to image/jpeg
            let mimeType = contentItem.mimeType;
            if (!mimeType) {
              // Use original content array (not filtered) to find filename
              const filename = findFilenameInContent(content);
              mimeType = getMimeTypeFromExtension(filename);
            }
            const isSvg = mimeType === "image/svg+xml";
            
            return (
              <div className="m-chat-bubble__image-wrapper" key={index}>
                <img
                  //className={`m-chat-bubble__image ${isSvg ? 'm-chat-bubble__image--svg' : ''}`}
                  //style={isSvg ? { maxWidth: '100%', height: 'auto' } : {}}
                  className="m-chat-bubble__image"
                  src={`data:${mimeType};base64,${contentItem.imageBytes}`}
                  alt={contentItem.imageTextDescription}
                />
              </div>
            );
          } else if (contentItem.type === "html") {
            return (
              <div
                dangerouslySetInnerHTML={{ __html: contentItem.html }}
                key={index}
              />
            );
          } else if (contentItem.type === "resource_reference") {
            return <ResourceReferenceRender key={index} contentItem={contentItem} />;
          } else {
            return <></>;
          }
        })}
    </>
  );
};

export default ChatEntryMsg;